import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  gmao,
  quotes as quotesApi,
  users as usersApi,
} from "@/api";
import { formatApiError } from "@/lib/api";
import { hasPageAccess } from "@/lib/accessPermissions";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  open: "Ouvert",
  taken: "Pris en charge",
  assigned: "Assigné",
  in_progress: "En cours",
  resolved: "Résolu",
  closed: "Fermé",
  cancelled: "Annulé",
};


// acta_patch_us_gmao_017_rma_v1
const RMA_STATUS_LABELS = {
  draft: "À préparer",
  shipped: "Expédié",
  supplier_received: "Reçu fournisseur",
  under_review: "En expertise",
  repaired: "Réparé",
  replaced: "Remplacé",
  rejected: "Refusé",
  returned_to_customer: "Retourné au client",
  closed: "Clôturé",
  cancelled: "Annulé",
};


const QUOTE_STATUS_LABELS = {
  draft: "Brouillon",
  pending_validation: "À valider",
  approved: "Validé",
  rejected: "Rejeté",
  sent: "Partagé au client",
  accepted: "Accepté",
  refused: "Refusé",
  purchase_order_created: "Bon de commande créé",
};


const RMA_NEXT_STATUS = {
  draft: "shipped",
  shipped: "supplier_received",
  supplier_received: "under_review",
  under_review: "repaired",
  repaired: "returned_to_customer",
  replaced: "returned_to_customer",
  rejected: "returned_to_customer",
  returned_to_customer: "closed",
};

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function minutesLabel(value) {
  if (value === null || value === undefined) return "—";
  if (value < 60) return `${value} min`;
  const h = Math.floor(value / 60);
  const m = value % 60;
  return m ? `${h} h ${m}` : `${h} h`;
}

function transitions(status) {
  if (status === "open") return ["in_progress", "resolved", "cancelled"];
  if (status === "taken" || status === "assigned") return ["in_progress", "resolved", "cancelled"];
  if (status === "in_progress") return ["resolved", "cancelled"];
  if (status === "resolved") return ["closed", "in_progress"];
  return [];
}

export default function GmaoSavTicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [history, setHistory] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [technicianId, setTechnicianId] = useState("");
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [rmaCases, setRmaCases] = useState([]);

  // acta_patch_us_gmao_019_sav_quote_v1
  const [linkedQuote, setLinkedQuote] =
    useState(null);
  const [showQuoteForm, setShowQuoteForm] =
    useState(false);
  const [quoteForm, setQuoteForm] = useState({
    section: "Prestations incluses",
    reference: "",
    description: "",
    quantity: "1",
    unit: "Unité(s)",
    unit_price: "",
    vat_rate: "20",
    notes: "",
  });

  const [showRmaForm, setShowRmaForm] = useState(false);
  const [rmaForm, setRmaForm] = useState({
    item_type: "machine",
    item_label: "",
    supplier: "",
    supplier_reference: "",
    rma_reference: "",
    reason: "",
    notes: "",
    carrier: "",
    tracking_number: "",
  });

  const canManage = ["admin", "manager", "maintenance_manager"].includes(user?.role);

  const canOpenIntervention =
    hasPageAccess(
      user,
      "gmao",
      "interventions"
    );

  const canCreateIntervention =
    canOpenIntervention &&
    [
      "admin",
      "manager",
      "maintenance_manager",
    ].includes(user?.role);

  const canPrepareQuote = [
    "admin",
    "manager",
  ].includes(user?.role);

  const isTechnician = user?.role === "technician";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        ticketData,
        historyData,
        attachmentsData,
        teamData,
        rmaData,
        quoteData,
      ] = await Promise.all([
        gmao.getSavTicket(ticketId),
        gmao.getSavTicketHistory(ticketId),
        gmao.listSavTicketAttachments(ticketId),
        canManage
          ? usersApi.list().catch(() => [])
          : Promise.resolve([]),
        gmao.listSavTicketRmaCases(ticketId),
        canPrepareQuote
          ? gmao
              .getSavTicketQuote(ticketId)
              .catch((error) => {
                if (
                  error.response?.status === 404
                ) {
                  return null;
                }

                throw error;
              })
          : Promise.resolve(null),
      ]);

      setTicket(ticketData);
      setHistory(Array.isArray(historyData) ? historyData : []);
      setAttachments(Array.isArray(attachmentsData) ? attachmentsData : []);
      setRmaCases(Array.isArray(rmaData) ? rmaData : []);
      setLinkedQuote(quoteData || null);

      const teamItems = Array.isArray(teamData)
        ? teamData
        : Array.isArray(teamData?.items)
          ? teamData.items
          : [];

      setTeam(teamItems);
      setTechnicianId(ticketData.assignedTechnicianId || "");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Impossible de charger le ticket SAV.");
    } finally {
      setLoading(false);
    }
  }, [
    canManage,
    canPrepareQuote,
    ticketId,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const technicians = useMemo(
    () => team.filter((item) => item.role === "technician" && item.isActive !== false),
    [team]
  );

  const take = async () => {
    setBusy(true);
    try {
      await gmao.takeSavTicket(ticket.id);
      await load();
      toast.success("Ticket pris en charge.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    setBusy(true);
    try {
      await gmao.assignSavTicketTechnician(ticket.id, technicianId, note);
      setNote("");
      await load();
      toast.success("Assignation mise à jour.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Assignation impossible.");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await gmao.updateSavTicketStatus(ticket.id, status, note);
      setNote("");
      await load();
      toast.success("Statut SAV mis à jour.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Changement de statut impossible.");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!note.trim()) {
      toast.error("Le commentaire est vide.");
      return;
    }

    setBusy(true);
    try {
      await gmao.addSavTicketComment(ticket.id, note);
      setNote("");
      await load();
      toast.success("Commentaire ajouté.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Commentaire impossible.");
    } finally {
      setBusy(false);
    }
  };


  const createInterventionFromSav = async () => {
    if (
      !ticket ||
      ticket.interventionId ||
      !canCreateIntervention
    ) {
      return;
    }

    setBusy(true);

    try {
      await gmao.createSavTicketIntervention(
        ticket.id
      );

      await load();

      toast.success(
        "Intervention corrective créée."
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Création de l’intervention impossible."
      );
    } finally {
      setBusy(false);
    }
  };


  const toggleQuoteForm = () => {
    setShowQuoteForm((current) => {
      const next = !current;

      if (
        next &&
        !quoteForm.description
      ) {
        setQuoteForm((form) => ({
          ...form,
          description:
            `Diagnostic SAV ${
              ticket?.ticketNumber || ""
            } - ${
              ticket?.machineName ||
              "Machine"
            }`.trim(),
          notes:
            ticket?.description || "",
        }));
      }

      return next;
    });
  };


  const updateQuoteForm = (
    key,
    value
  ) => {
    setQuoteForm((current) => ({
      ...current,
      [key]: value,
    }));
  };


  const createQuoteFromSav = async () => {
    if (!ticket?.prospectId) {
      toast.error(
        "Le ticket SAV n’est lié à aucun client."
      );
      return;
    }

    if (!quoteForm.description.trim()) {
      toast.error(
        "Renseignez la prestation ou le matériel."
      );
      return;
    }

    const quantity = Number(
      quoteForm.quantity
    );
    const unitPrice = Number(
      quoteForm.unit_price
    );
    const vatRate = Number(
      quoteForm.vat_rate
    );

    if (
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      toast.error("Quantité invalide.");
      return;
    }

    if (
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    ) {
      toast.error(
        "Prix unitaire HT invalide."
      );
      return;
    }

    if (
      !Number.isFinite(vatRate) ||
      vatRate < 0 ||
      vatRate > 100
    ) {
      toast.error("TVA invalide.");
      return;
    }

    setBusy(true);

    try {
      await quotesApi.create({
        prospect_id: ticket.prospectId,
        client_id:
          ticket.lifecycleStage === "client"
            ? ticket.prospectId
            : null,
        amount: quantity * unitPrice,
        currency: "EUR",
        vat_rate: vatRate,
        include_terms: true,
        notes: quoteForm.notes || "",
        source: "sav",
        sav_ticket_id: ticket.id,
        machine_id:
          ticket.machineId || null,
        intervention_id:
          ticket.interventionId || null,
        machine_name:
          ticket.machineName || "",
        lines: [
          {
            section:
              quoteForm.section ||
              "Prestations incluses",
            reference:
              quoteForm.reference || "",
            description:
              quoteForm.description,
            quantity,
            unit:
              quoteForm.unit ||
              "Unité(s)",
            unit_price: unitPrice,
            vat_rate: vatRate,
            notes:
              quoteForm.notes || "",
          },
        ],
      });

      setShowQuoteForm(false);
      await load();

      toast.success(
        "Devis SAV créé en brouillon."
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Création du devis SAV impossible."
      );
    } finally {
      setBusy(false);
    }
  };


  const createRma = async () => {
    if (
      !rmaForm.item_label.trim() ||
      !rmaForm.supplier.trim() ||
      !rmaForm.reason.trim()
    ) {
      toast.error(
        "Renseignez l’équipement, le fournisseur et le motif."
      );
      return;
    }

    setBusy(true);

    try {
      await gmao.createSavTicketRma(
        ticket.id,
        rmaForm
      );

      setShowRmaForm(false);
      setRmaForm({
        item_type: "machine",
        item_label: "",
        supplier: "",
        supplier_reference: "",
        rma_reference: "",
        reason: "",
        notes: "",
        carrier: "",
        tracking_number: "",
      });

      await load();
      toast.success("RMA fournisseur créé.");
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Création du RMA impossible."
      );
    } finally {
      setBusy(false);
    }
  };


  const advanceRma = async (
    rma
  ) => {
    const nextStatus =
      RMA_NEXT_STATUS[
        rma.status
      ];

    if (!nextStatus) {
      return;
    }

    setBusy(true);

    try {
      await gmao.updateSavTicketRma(
        ticket.id,
        rma.id,
        {
          status: nextStatus,
          note: note,
          carrier: rma.carrier || "",
          tracking_number:
            rma.trackingNumber || "",
          rma_reference:
            rma.rmaReference || "",
        }
      );

      setNote("");
      await load();
      toast.success(
        "Statut RMA mis à jour."
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Mise à jour RMA impossible."
      );
    } finally {
      setBusy(false);
    }
  };


  const downloadAttachment = async (attachment) => {
    setBusy(true);

    try {
      const blob = await gmao.getSavTicketAttachmentBlob(ticket.id, attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = attachment.name || "piece-jointe-sav";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Téléchargement impossible.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <main className="p-4 sm:p-6 lg:p-8">Chargement du ticket SAV…</main>;
  if (!ticket) return <main className="p-4 sm:p-6 lg:p-8">Ticket SAV introuvable.</main>;

  return (
    <main className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8" data-testid="sav-ticket-detail">
      <Link to="/app/gmao/sav" className="text-sm font-semibold text-[var(--do-blue)]">← Retour aux tickets SAV</Link>

      <div className="mt-4 flex flex-col gap-5 xl:flex-row xl:items-start">
        <section className="lirah-card flex-1 p-5 sm:p-6">
          <div className="label-eyebrow">Ticket SAV</div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-3xl font-black">{ticket.ticketNumber}</h1>
            <span className="rounded-full border border-[var(--do-border)] px-3 py-1 text-xs font-bold">
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
            <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-bold text-[var(--do-accent)]">{ticket.urgency}</span>
          </div>

          <p className="mt-4 whitespace-pre-line text-sm">{ticket.description}</p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Info label="Client" value={ticket.clientName} />
            <Info label="Machine" value={ticket.machineName} />
            <Info label="Technicien" value={ticket.technicianName} />
            <Info label="Créé le" value={formatDate(ticket.createdAt)} />
            <Info label="1ère réponse" value={minutesLabel(ticket.sla?.firstResponseMinutes)} />
            <Info label="Résolution" value={minutesLabel(ticket.sla?.resolutionMinutes)} />
            <Info label="Temps écoulé" value={minutesLabel(ticket.sla?.elapsedMinutes)} />
            <Info
              label="Intervention"
              value={
                ticket.interventionStatus ||
                (
                  ticket.interventionId
                    ? "Intervention liée"
                    : "Aucune intervention liée"
                )
              }
            />
          </div>

          {ticket.interventionId &&
          canOpenIntervention ? (
            <Link
              to={`/app/gmao/interventions/${encodeURIComponent(ticket.interventionId)}`}
              className="mt-5 inline-flex rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold"
              data-testid="sav-ticket-open-intervention"
            >
              Voir la fiche d’intervention
            </Link>
          ) : (
            !ticket.interventionId &&
            canCreateIntervention &&
            ![
              "closed",
              "cancelled",
            ].includes(ticket.status) && (
              <button
                type="button"
                onClick={createInterventionFromSav}
                disabled={busy}
                className="mt-5 inline-flex rounded-xl bg-[var(--do-blue)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
                data-testid="sav-ticket-create-intervention"
              >
                Créer une intervention
              </button>
            )
          )}
        </section>

        <aside className="lirah-card w-full p-5 xl:max-w-sm">
          <h2 className="font-heading text-xl font-black">Actions</h2>

          <div className="mt-4 space-y-3">
            {canManage && ticket.status === "open" && (
              <button
                type="button"
                onClick={take}
                disabled={busy}
                className="w-full rounded-xl bg-[var(--do-blue)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
                data-testid="sav-ticket-take"
              >
                Prendre en charge
              </button>
            )}

            {canManage && (
              <div className="rounded-2xl border border-[var(--do-border)] p-3">
                <label className="text-sm font-bold">
                  Technicien
                  <select
                    value={technicianId}
                    onChange={(event) => setTechnicianId(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
                    data-testid="sav-ticket-technician-select"
                  >
                    <option value="">Non affecté</option>
                    {technicians.map((technician) => (
                      <option key={technician.id} value={technician.id}>
                        {[technician.firstName, technician.lastName].filter(Boolean).join(" ") || technician.email}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={assign}
                  disabled={busy}
                  className="mt-3 w-full rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold disabled:opacity-60"
                  data-testid="sav-ticket-assign"
                >
                  Mettre à jour l’assignation
                </button>
              </div>
            )}

            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2 text-sm"
              placeholder="Note interne / motif / commentaire…"
              data-testid="sav-ticket-note"
            />

            <button
              type="button"
              onClick={addComment}
              disabled={busy}
              className="w-full rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold disabled:opacity-60"
            >
              Ajouter un commentaire
            </button>

            <div className="grid gap-2">
              {transitions(ticket.status).map((status) => {
                const technicianAllowed = !isTechnician || ["in_progress", "resolved"].includes(status);
                if (!canManage && !technicianAllowed) return null;

                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => changeStatus(status)}
                    disabled={busy}
                    className="rounded-xl bg-[var(--do-ink)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
                    data-testid={`sav-ticket-status-${status}`}
                  >
                    Passer en {STATUS_LABELS[status]}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      </div>


      <section
        className="lirah-card mt-6 p-5 sm:p-6"
        data-testid="sav-ticket-quote"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="label-eyebrow">
              Commercial
            </div>
            <h2 className="font-heading text-xl font-black">
              Devis lié au ticket
            </h2>
          </div>

          {canPrepareQuote &&
            !linkedQuote && (
              <button
                type="button"
                onClick={toggleQuoteForm}
                disabled={busy}
                className="rounded-xl bg-[var(--do-blue)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
                data-testid="sav-ticket-create-quote"
              >
                Préparer un devis
              </button>
            )}
        </div>

        {linkedQuote ? (
          <article
            className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
            data-testid="sav-ticket-linked-quote"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="font-heading text-lg font-black">
                  {linkedQuote.quoteNumber ||
                    "Devis SAV"}
                </div>

                <div className="mt-1 text-sm text-[var(--do-muted)]">
                  {linkedQuote.customerName ||
                    ticket.clientName}
                  {" · "}
                  {Number(
                    linkedQuote.totalTtc ||
                      linkedQuote.amount ||
                      0
                  ).toLocaleString(
                    "fr-FR",
                    {
                      style: "currency",
                      currency:
                        linkedQuote.currency ||
                        "EUR",
                    }
                  )}
                </div>
              </div>

              <span className="rounded-full border border-[var(--do-border)] px-3 py-1 text-xs font-bold">
                {QUOTE_STATUS_LABELS[
                  linkedQuote.status
                ] ||
                  linkedQuote.status}
              </span>
            </div>

            <Link
              to="/app/commerce/quotes"
              className="mt-4 inline-flex rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold"
              data-testid="sav-ticket-open-quote"
            >
              Ouvrir dans les devis
            </Link>
          </article>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--do-border)] p-5 text-sm text-[var(--do-muted)]">
            Aucun devis n’est encore lié à ce ticket.
          </div>
        )}

        {showQuoteForm &&
          !linkedQuote && (
            <div
              className="mt-5 grid gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4 sm:grid-cols-2"
              data-testid="sav-ticket-quote-form"
            >
              <select
                value={quoteForm.section}
                onChange={(event) =>
                  updateQuoteForm(
                    "section",
                    event.target.value
                  )
                }
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
              >
                <option value="Prestations incluses">
                  Prestation SAV
                </option>
                <option value="Pièces à Main">
                  Pièce à main
                </option>
                <option value="Care / maintenance">
                  Maintenance
                </option>
                <option value="Autre">
                  Autre
                </option>
              </select>

              <input
                value={quoteForm.reference}
                onChange={(event) =>
                  updateQuoteForm(
                    "reference",
                    event.target.value
                  )
                }
                placeholder="Référence"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
              />

              <input
                value={quoteForm.description}
                onChange={(event) =>
                  updateQuoteForm(
                    "description",
                    event.target.value
                  )
                }
                placeholder="Prestation ou matériel"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2 sm:col-span-2"
                data-testid="sav-ticket-quote-description"
              />

              <input
                type="number"
                min="1"
                step="1"
                value={quoteForm.quantity}
                onChange={(event) =>
                  updateQuoteForm(
                    "quantity",
                    event.target.value
                  )
                }
                placeholder="Quantité"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
              />

              <input
                value={quoteForm.unit}
                onChange={(event) =>
                  updateQuoteForm(
                    "unit",
                    event.target.value
                  )
                }
                placeholder="Unité"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
              />

              <input
                type="number"
                min="0"
                step="0.01"
                value={quoteForm.unit_price}
                onChange={(event) =>
                  updateQuoteForm(
                    "unit_price",
                    event.target.value
                  )
                }
                placeholder="Prix unitaire HT"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
                data-testid="sav-ticket-quote-price"
              />

              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={quoteForm.vat_rate}
                onChange={(event) =>
                  updateQuoteForm(
                    "vat_rate",
                    event.target.value
                  )
                }
                placeholder="TVA %"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
              />

              <textarea
                value={quoteForm.notes}
                onChange={(event) =>
                  updateQuoteForm(
                    "notes",
                    event.target.value
                  )
                }
                rows={4}
                placeholder="Diagnostic, pièces nécessaires et informations internes"
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2 sm:col-span-2"
              />

              <button
                type="button"
                onClick={createQuoteFromSav}
                disabled={busy}
                className="rounded-xl bg-[var(--do-ink)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60 sm:col-span-2"
                data-testid="sav-ticket-quote-submit"
              >
                Créer le devis brouillon
              </button>
            </div>
          )}
      </section>


      <section
        className="lirah-card mt-6 p-5 sm:p-6"
        data-testid="sav-ticket-rma"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="label-eyebrow">
              Retour fournisseur
            </div>
            <h2 className="font-heading text-xl font-black">
              RMA
            </h2>
          </div>

          {canManage && (
            <button
              type="button"
              onClick={() =>
                setShowRmaForm(
                  (current) =>
                    !current
                )
              }
              disabled={busy}
              className="rounded-xl bg-[var(--do-blue)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
              data-testid="sav-ticket-create-rma"
            >
              Créer un RMA
            </button>
          )}
        </div>

        {showRmaForm && (
          <div
            className="mt-5 grid gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4 sm:grid-cols-2"
            data-testid="sav-ticket-rma-form"
          >
            <select
              value={
                rmaForm.item_type
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    item_type:
                      event.target.value,
                  })
                )
              }
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            >
              <option value="machine">
                Machine complète
              </option>
              <option value="part">
                Pièce détachée
              </option>
              <option value="handpiece">
                Pièce à main
              </option>
              <option value="other">
                Autre équipement
              </option>
            </select>

            <input
              value={
                rmaForm.item_label
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    item_label:
                      event.target.value,
                  })
                )
              }
              placeholder="Équipement retourné"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            />

            <input
              value={
                rmaForm.supplier
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    supplier:
                      event.target.value,
                  })
                )
              }
              placeholder="Fournisseur"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            />

            <input
              value={
                rmaForm.rma_reference
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    rma_reference:
                      event.target.value,
                  })
                )
              }
              placeholder="Référence RMA"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            />

            <input
              value={
                rmaForm.carrier
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    carrier:
                      event.target.value,
                  })
                )
              }
              placeholder="Transporteur"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            />

            <input
              value={
                rmaForm.tracking_number
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    tracking_number:
                      event.target.value,
                  })
                )
              }
              placeholder="Numéro de suivi"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2"
            />

            <textarea
              value={
                rmaForm.reason
              }
              onChange={(event) =>
                setRmaForm(
                  (current) => ({
                    ...current,
                    reason:
                      event.target.value,
                  })
                )
              }
              rows={3}
              placeholder="Motif du retour fournisseur"
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] px-3 py-2 sm:col-span-2"
            />

            <button
              type="button"
              onClick={createRma}
              disabled={busy}
              className="rounded-xl bg-[var(--do-ink)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60 sm:col-span-2"
              data-testid="sav-ticket-rma-submit"
            >
              Enregistrer le RMA
            </button>
          </div>
        )}

        {rmaCases.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--do-border)] p-5 text-sm text-[var(--do-muted)]">
            Aucun retour fournisseur lié à ce ticket.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {rmaCases.map(
              (rma) => {
                const nextStatus =
                  RMA_NEXT_STATUS[
                    rma.status
                  ];

                return (
                  <article
                    key={rma.id}
                    className="rounded-2xl border border-[var(--do-border)] p-4"
                    data-testid="sav-ticket-rma-row"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="font-heading font-black">
                          {rma.itemLabel}
                        </div>

                        <div className="mt-1 text-sm text-[var(--do-muted)]">
                          {rma.supplier}
                          {rma.rmaReference
                            ? ` · RMA ${rma.rmaReference}`
                            : ""}
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {rma.carrier || "Transporteur non renseigné"}
                          {rma.trackingNumber
                            ? ` · ${rma.trackingNumber}`
                            : ""}
                        </div>
                      </div>

                      <span className="rounded-full border border-[var(--do-border)] px-3 py-1 text-xs font-bold">
                        {RMA_STATUS_LABELS[
                          rma.status
                        ] ||
                          rma.status}
                      </span>
                    </div>

                    <p className="mt-3 whitespace-pre-line text-sm">
                      {rma.reason}
                    </p>

                    {canManage &&
                      nextStatus && (
                        <button
                          type="button"
                          onClick={() =>
                            advanceRma(
                              rma
                            )
                          }
                          disabled={busy}
                          className="mt-4 rounded-xl bg-[var(--do-ink)] px-4 py-2 text-sm font-bold text-[var(--do-on-accent)] disabled:opacity-60"
                          data-testid="sav-ticket-rma-next-status"
                        >
                          Passer en{" "}
                          {RMA_STATUS_LABELS[
                            nextStatus
                          ]}
                        </button>
                      )}
                  </article>
                );
              }
            )}
          </div>
        )}
      </section>

      <section className="lirah-card mt-6 p-5 sm:p-6" data-testid="sav-ticket-attachments">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="label-eyebrow">Client</div>
            <h2 className="font-heading text-xl font-black">Pièces jointes SAV</h2>
          </div>

          <span className="rounded-full border border-[var(--do-border)] px-3 py-1 text-xs font-bold">
            {attachments.length} fichier(s)
          </span>
        </div>

        {attachments.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--do-border)] p-5 text-sm text-[var(--do-muted)]">
            Aucune photo ou pièce jointe transmise par le client.
          </div>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {attachments.map((attachment) => (
              <article
                key={attachment.id}
                className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
                data-testid="sav-ticket-attachment"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-heading text-sm font-black">
                      {attachment.name}
                    </div>

                    <div className="mt-1 text-xs text-[var(--do-muted)]">
                      {attachment.kind === "photo" ? "Photo" : "Document"} · {attachment.extension?.toUpperCase()} · {Math.ceil((attachment.size || 0) / 1024)} Ko
                    </div>
                  </div>

                  <span className="rounded-full bg-[var(--do-bg-solid)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--do-muted)]">
                    {attachment.kind || "fichier"}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => downloadAttachment(attachment)}
                  disabled={busy}
                  className="mt-4 w-full rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold disabled:opacity-60"
                  data-testid="sav-ticket-attachment-download"
                >
                  Télécharger
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="lirah-card mt-6 p-5 sm:p-6">
        <h2 className="font-heading text-xl font-black">Historique du ticket</h2>

        <div className="mt-4 space-y-3" data-testid="sav-ticket-history">
          {history.map((event) => (
            <article key={event.id} className="rounded-2xl border border-[var(--do-border)] p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <strong>{event.eventType}</strong>
                <span className="text-xs text-[var(--do-muted)]">{formatDate(event.createdAt)}</span>
              </div>

              <p className="mt-1 text-sm text-[var(--do-muted)]">
                Par {event.actorName || event.actorUserId || "—"}
                {event.fromStatus || event.toStatus ? ` · ${event.fromStatus || "—"} → ${event.toStatus || "—"}` : ""}
              </p>

              {event.details?.note && <p className="mt-2 whitespace-pre-line text-sm">{event.details.note}</p>}
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3">
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--do-muted)]">{label}</div>
      <div className="mt-1 text-sm font-semibold">{value || "—"}</div>
    </div>
  );
}
